import { DiagnosticCardData } from './diagnosticCard';
import { PackResolver } from './resolution';
import {
  ALL,
  Pack,
  UnitProgress,
  aggregateProgress,
  completionBucket,
  isPackInClassroomScope,
  lastActiveFor,
  nameMatchesSearch,
  packAssignedEpochFor,
} from './shared';

export const OPEN = 'Open'
export const CLOSED = 'Closed'

export const ANY_COMPLETION_OPTION = { label: 'All', value: ALL }

export const COMPLETION_OPTIONS = [
  ANY_COMPLETION_OPTION,
  { label: 'Not started', value: 'not_started' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
]

export const GROUP_BY_PACK_TYPE = 'pack_type'
export const GROUP_BY_NONE = 'none'
export const GROUP_BY_OPTIONS = [
  { label: 'Pack type', value: GROUP_BY_PACK_TYPE },
  { label: 'No grouping', value: GROUP_BY_NONE },
]

export const SORT_NEWEST_ASSIGNED = 'newest_assigned'
export const SORT_OLDEST_ASSIGNED = 'oldest_assigned'
export const SORT_RECENTLY_ACTIVE = 'recently_active'
export const SORT_LEAST_ACTIVE = 'least_recently_active'
export const SORT_OPTIONS = [
  { label: 'Newest assigned', value: SORT_NEWEST_ASSIGNED },
  { label: 'Oldest assigned', value: SORT_OLDEST_ASSIGNED },
  { label: 'Recently active', value: SORT_RECENTLY_ACTIVE },
  { label: 'Least recently active', value: SORT_LEAST_ACTIVE },
]
export const ACTIVITY_SORTS = [SORT_RECENTLY_ACTIVE, SORT_LEAST_ACTIVE]
export const ASCENDING_SORTS = [SORT_OLDEST_ASSIGNED, SORT_LEAST_ACTIVE]

export function createFilters({ view, selectedClassroomId, selectedCompletion, searchQuery, sortBy, unitProgress, packResolver }: {
  view: string,
  selectedClassroomId: string | number | null,
  selectedCompletion: string,
  searchQuery: string,
  sortBy: string,
  unitProgress: Record<string, UnitProgress>,
  packResolver: PackResolver,
}) {
  const { backingPackFor, postBackingPackFor } = packResolver

  function completionStatusFor(pack: Pack): string {
    const progress = aggregateProgress(pack, unitProgress[pack.unitId], selectedClassroomId)
    return completionBucket(progress.completedAny, progress.completedAll, progress.assigned)
  }

  function packMatchesFilters(pack: Pack): boolean {
    const packIsOpen = pack.open !== false
    if (packIsOpen !== (view === OPEN)) { return false }
    if (!isPackInClassroomScope(pack, selectedClassroomId)) { return false }
    if (selectedCompletion !== ALL && completionStatusFor(pack) !== selectedCompletion) { return false }
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const matchesName = pack.unitName.toLowerCase().includes(query)
      const matchesActivity = pack.activities.some(a => nameMatchesSearch(a.name, searchQuery))
      const defaultName = unitProgress[pack.unitId]?.template_name
      const matchesDefaultName = Boolean(defaultName && defaultName.toLowerCase().includes(query))
      if (!matchesName && !matchesActivity && !matchesDefaultName) { return false }
    }
    return true
  }

  function diagnosticCardMatchesFilters(card: DiagnosticCardData): boolean {
    if (selectedClassroomId !== ALL && String(card.classroomId) !== String(selectedClassroomId)) { return false }
    if (selectedCompletion !== ALL) {
      const statusOf = (record: { completed_count: number, assigned_count: number }) =>
        completionBucket(record.completed_count, record.completed_count, record.assigned_count)
      const post = card.post as { completed_count?: number, assigned_count?: number } | undefined
      const postAssigned = Boolean(post && post.assigned_count !== undefined && post.assigned_count > 0)
      const matchesEitherPhase = statusOf(card.pre) === selectedCompletion ||
        (postAssigned && statusOf(post as { completed_count: number, assigned_count: number }) === selectedCompletion)
      if (!matchesEitherPhase) { return false }
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const matchesDiagnosticName = card.name.toLowerCase().includes(query)
      const matchesActivityName = [card.pre.activity_name, card.post?.activity_name]
        .some(name => nameMatchesSearch(name, searchQuery))
      const matchesPackName = [backingPackFor(card), postBackingPackFor(card)]
        .some(pack => pack && pack.unitName.toLowerCase().includes(query))
      if (!matchesDiagnosticName && !matchesActivityName && !matchesPackName) { return false }
    }
    return true
  }

  function packSortTime(pack: Pack): number {
    if (ACTIVITY_SORTS.includes(sortBy)) {
      const lastActive = lastActiveFor(unitProgress[pack.unitId], selectedClassroomId)
      return lastActive ? new Date(lastActive).getTime() : 0
    }
    return packAssignedEpochFor(pack, selectedClassroomId) * 1000
  }
  const sortAscending = ASCENDING_SORTS.includes(sortBy)
  function compareBySort(aTime: number, bTime: number): number {
    return sortAscending ? aTime - bTime : bTime - aTime
  }
  function sortByTime<T>(items: T[], timeOf: (item: T) => number): T[] {
    return items
      .map(item => [timeOf(item), item] as [number, T])
      .sort(([aTime], [bTime]) => compareBySort(aTime, bTime))
      .map(([, item]) => item)
  }
  function sortPacks(list: Pack[]): Pack[] {
    return sortByTime(list, packSortTime)
  }

  function diagnosticTime(card: DiagnosticCardData): number {
    const backingPack = backingPackFor(card)
    if (ACTIVITY_SORTS.includes(sortBy)) {
      const lastActive = backingPack ? lastActiveFor(unitProgress[backingPack.unitId], selectedClassroomId) : null
      return lastActive ? new Date(lastActive).getTime() : 0
    }
    return card.pre.assigned_date ? new Date(card.pre.assigned_date).getTime() : 0
  }

  return {
    compareBySort,
    diagnosticCardMatchesFilters,
    diagnosticTime,
    packMatchesFilters,
    packSortTime,
    sortByTime,
    sortPacks,
  }
}
